import React from 'react';
import './PrivacyPolicy.css';

export const PrivacyPolicy: React.FC = () => {
  return (
    <div className="privacy-policy">
      <div className="privacy-header">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last updated: {new Date().toLocaleDateString()}</p>
      </div>

      <div className="privacy-content">
        <section>
          <h2>1. Introduction</h2>
          <p>
            BigSystemOne ("we," "our," or "us") operates restaurant management software and services 
            (the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard 
            your information when you use our Service, including our web application, mobile applications, 
            and point-of-sale systems.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Information You Provide</h3>
          <ul>
            <li><strong>Account Information:</strong> Name, email address, phone number, business information</li>
            <li><strong>Business Data:</strong> Restaurant information, menu items, pricing, store locations</li>
            <li><strong>Employee Information:</strong> Names, contact details, roles, schedules, photos (with consent)</li>
            <li><strong>Customer Data:</strong> Order information, payment details (processed securely), preferences</li>
            <li><strong>Financial Information:</strong> Sales data, transaction records, tax information</li>
          </ul>

          <h3>2.2 Information We Collect Automatically</h3>
          <ul>
            <li><strong>Usage Data:</strong> How you interact with our Service, features used, time spent</li>
            <li><strong>Device Information:</strong> Device type, operating system, browser type, IP address</li>
            <li><strong>Location Data:</strong> GPS coordinates for delivery optimization and store management</li>
            <li><strong>Analytics Data:</strong> Performance metrics, error logs, user behavior patterns</li>
          </ul>

          <h3>2.3 Third-Party Integrations</h3>
          <ul>
            <li><strong>Social Media:</strong> When you connect social media accounts, we access posting permissions</li>
            <li><strong>Payment Processors:</strong> Transaction data processed through secure payment gateways</li>
            <li><strong>Weather Services:</strong> Location-based weather data for operational planning</li>
            <li><strong>AI Services:</strong> Menu item descriptions and images for content generation</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          
          <h3>3.1 Service Operations</h3>
          <ul>
            <li>Provide and maintain our restaurant management services</li>
            <li>Process transactions and manage orders</li>
            <li>Generate reports and analytics for business insights</li>
            <li>Manage employee schedules and payroll integration</li>
          </ul>

          <h3>3.2 Social Media Management</h3>
          <ul>
            <li>Publish posts to connected social media accounts</li>
            <li>Schedule and manage marketing campaigns</li>
            <li>Generate and distribute promotional discount codes</li>
            <li>Track engagement and campaign performance</li>
          </ul>

          <h3>3.3 Communication and Support</h3>
          <ul>
            <li>Send service updates, security alerts, and support messages</li>
            <li>Respond to inquiries and provide customer support</li>
            <li>Send promotional materials (with your consent)</li>
          </ul>

          <h3>3.4 Legal and Security</h3>
          <ul>
            <li>Comply with applicable laws and regulations</li>
            <li>Detect and prevent fraud and security breaches</li>
            <li>Protect our rights and the rights of our users</li>
          </ul>
        </section>

        <section>
          <h2>4. Information Sharing and Disclosure</h2>
          
          <h3>4.1 We Do Not Sell Personal Information</h3>
          <p>We do not sell, rent, or trade your personal information to third parties for monetary consideration.</p>

          <h3>4.2 Service Providers</h3>
          <p>We may share information with trusted service providers who assist us in operating our Service:</p>
          <ul>
            <li>Cloud hosting and data storage providers (AWS)</li>
            <li>Payment processing services</li>
            <li>Analytics and performance monitoring services</li>
            <li>Customer support platforms</li>
            <li>AI and machine learning services for content generation</li>
          </ul>

          <h3>4.3 Social Media Platforms</h3>
          <p>When you authorize social media integrations, we share content with:</p>
          <ul>
            <li>Facebook/Meta (posts, campaigns, analytics)</li>
            <li>Instagram (image and video content)</li>
            <li>Twitter/X (text posts and media)</li>
            <li>LinkedIn (business content)</li>
            <li>TikTok (video content)</li>
          </ul>

          <h3>4.4 Legal Requirements</h3>
          <p>We may disclose information when required by law or to:</p>
          <ul>
            <li>Comply with legal processes or government requests</li>
            <li>Enforce our terms of service</li>
            <li>Protect the safety and rights of our users and the public</li>
            <li>Prevent fraud or illegal activities</li>
          </ul>
        </section>

        <section>
          <h2>5. Data Security</h2>
          
          <h3>5.1 Security Measures</h3>
          <ul>
            <li>Encryption of data in transit and at rest</li>
            <li>Multi-factor authentication for account access</li>
            <li>Regular security audits and vulnerability assessments</li>
            <li>Employee training on data protection best practices</li>
            <li>Secure cloud infrastructure with AWS</li>
          </ul>

          <h3>5.2 Payment Security</h3>
          <ul>
            <li>PCI DSS compliance for payment processing</li>
            <li>Tokenization of sensitive payment information</li>
            <li>Secure transmission of financial data</li>
          </ul>
        </section>

        <section>
          <h2>6. Your Privacy Rights</h2>
          
          <h3>6.1 Access and Control</h3>
          <p>You have the right to:</p>
          <ul>
            <li>Access your personal information we hold</li>
            <li>Update or correct inaccurate information</li>
            <li>Request deletion of your personal information</li>
            <li>Export your data in a portable format</li>
            <li>Opt-out of promotional communications</li>
          </ul>

          <h3>6.2 Social Media Permissions</h3>
          <p>You can:</p>
          <ul>
            <li>Revoke social media account connections at any time</li>
            <li>Control which platforms receive your content</li>
            <li>Manage posting schedules and campaigns</li>
            <li>View analytics and engagement data</li>
          </ul>

          <h3>6.3 California Privacy Rights (CCPA)</h3>
          <p>California residents have additional rights including:</p>
          <ul>
            <li>Right to know what personal information is collected</li>
            <li>Right to delete personal information</li>
            <li>Right to opt-out of the sale of personal information</li>
            <li>Right to non-discrimination for exercising privacy rights</li>
          </ul>

          <h3>6.4 European Privacy Rights (GDPR)</h3>
          <p>EU residents have rights including:</p>
          <ul>
            <li>Right of access to personal data</li>
            <li>Right to rectification of inaccurate data</li>
            <li>Right to erasure ("right to be forgotten")</li>
            <li>Right to data portability</li>
            <li>Right to object to processing</li>
          </ul>
        </section>

        <section>
          <h2>7. Data Retention</h2>
          <p>We retain your information for as long as necessary to:</p>
          <ul>
            <li>Provide our services to you</li>
            <li>Comply with legal obligations</li>
            <li>Resolve disputes and enforce agreements</li>
            <li>Maintain business records for tax and accounting purposes</li>
          </ul>
          <p>
            When you terminate your account, we will delete or anonymize your personal information 
            within 90 days, except where retention is required by law.
          </p>
        </section>

        <section>
          <h2>8. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than your own. 
            We ensure appropriate safeguards are in place, including:
          </p>
          <ul>
            <li>Standard contractual clauses approved by relevant authorities</li>
            <li>Adequacy decisions by regulatory bodies</li>
            <li>Certification under recognized privacy frameworks</li>
          </ul>
        </section>

        <section>
          <h2>9. Children's Privacy</h2>
          <p>
            Our Service is not intended for individuals under the age of 16. We do not knowingly 
            collect personal information from children under 16. If we become aware that we have 
            collected such information, we will take steps to delete it promptly.
          </p>
        </section>

        <section>
          <h2>10. Cookies and Tracking Technologies</h2>
          
          <h3>10.1 Types of Cookies</h3>
          <ul>
            <li><strong>Essential Cookies:</strong> Required for basic functionality</li>
            <li><strong>Performance Cookies:</strong> Help us analyze usage and improve performance</li>
            <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
            <li><strong>Marketing Cookies:</strong> Used for targeted advertising (with consent)</li>
          </ul>

          <h3>10.2 Cookie Management</h3>
          <p>
            You can control cookies through your browser settings. Note that disabling certain 
            cookies may affect the functionality of our Service.
          </p>
        </section>

        <section>
          <h2>11. Third-Party Links</h2>
          <p>
            Our Service may contain links to third-party websites or services. We are not responsible 
            for the privacy practices of these third parties. We encourage you to review their 
            privacy policies before providing any information.
          </p>
        </section>

        <section>
          <h2>12. Updates to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of material changes by:
          </p>
          <ul>
            <li>Posting the updated policy on our website</li>
            <li>Sending email notifications to registered users</li>
            <li>Displaying prominent notices in our Service</li>
          </ul>
          <p>Your continued use of our Service after changes become effective constitutes acceptance of the updated policy.</p>
        </section>

        <section>
          <h2>13. Contact Information</h2>
          <p>If you have questions about this Privacy Policy or our privacy practices, please contact us:</p>
          
          <div className="contact-info">
            <p><strong>BigSystemOne Privacy Team</strong></p>
            <p>Email: <a href="mailto:privacy@bigsystemone.com">privacy@bigsystemone.com</a></p>
            <p>Phone: 1-800-BIG-SYS1 (1-800-244-7971)</p>
            <p>
              Mailing Address:<br />
              BigSystemOne, Inc.<br />
              Privacy Department<br />
              [Your Business Address]<br />
              [City, State, ZIP Code]
            </p>
          </div>

          <p>
            For EU residents, you may also contact our Data Protection Officer at: 
            <a href="mailto:dpo@bigsystemone.com">dpo@bigsystemone.com</a>
          </p>
        </section>

        <section>
          <h2>14. Effective Date</h2>
          <p>This Privacy Policy is effective as of {new Date().toLocaleDateString()} and was last updated on {new Date().toLocaleDateString()}.</p>
        </section>
      </div>
    </div>
  );
};